import React from 'react'

import { IntercomProvider as BaseIntercomProvider } from 'react-use-intercom'

import { useAssertContext } from '@hooks'

import { getIntercomAppId } from './helpers'

function VendorIntercomProvider({
  children,
  onUnreadCountChange,
}: {
  children: NonNullable<React.ReactNode> | NonNullable<React.ReactNode>[]
  onUnreadCountChange: (count: number) => void
}) {
  const appId = getIntercomAppId()

  if (!appId && process.env.NODE_ENV !== 'test') {
    console.warn("Intercom won't be initialized because Intercom's appId is not provided")
  }

  // Every company loads Intercom, hubspot-mode ones included: that is how an autotax customer can be
  // shown an Intercom message. What differs by communication mode is whether anything in the UI can
  // open the messenger - see CompanyBasedIntercomInitializer.
  return (
    <BaseIntercomProvider
      appId={appId || 'intercom-app-id-not-set'}
      apiBase="https://api-iam.eu.intercom.io"
      shouldInitialize={Boolean(appId)}
      onUnreadCountChange={onUnreadCountChange}
    >
      {children}
    </BaseIntercomProvider>
  )
}

const AppIntercomContext = React.createContext({ unreadMessagesCount: 0 })

AppIntercomContext.displayName = 'AppIntercomContext'

interface AppIntercomProviderProps {
  children: NonNullable<React.ReactNode> | NonNullable<React.ReactNode>[]
}

export function AppIntercomProvider({ children }: AppIntercomProviderProps) {
  const [unreadMessagesCount, setUnreadMessagesCount] = React.useState(0)

  const onUnreadCountChange = React.useCallback((amount: number) => {
    setUnreadMessagesCount(amount)
  }, [])

  return (
    <VendorIntercomProvider onUnreadCountChange={onUnreadCountChange}>
      <AppIntercomContext.Provider value={{ unreadMessagesCount }}>{children}</AppIntercomContext.Provider>
    </VendorIntercomProvider>
  )
}

export function useIntercomUnreadMessagesCount() {
  return useAssertContext(AppIntercomContext).unreadMessagesCount
}
